import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import {
  EvchargeTelemetryState,
  System,
  getEvchargeTelemetryStatus,
  isNotNullOrUndefined,
  productProperty
} from 'ngx-core-services';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  getStrategies,
  getLabels,
  getEnumKeyName,
  getStatusDetails,
} from './evcharge-dashboard.utils';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import { SystemsState, selectSelectedEvcharge, selectedSystemUid } from '../../store';
@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-550': 550,
})
@Component({
  selector: 'lib-evcharge-dashboard',
  templateUrl: './evcharge-dashboard.component.html',
  styleUrls: ['./evcharge-dashboard.component.scss'],
})
export class EvchargeDashboardComponent implements ICustomBlock, IReduxBlock {

  readonly _externalProperties$ = new BehaviorSubject<productProperty[] | undefined>(undefined);
  @Input() set externalDevices(properties: productProperty[] | undefined) {
    this._externalProperties$.next(properties);
  }

  @Input() uiFramework: 'material' | 'ionic' = 'material';

  @Input() productImageSrc?: string;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  _labels = getLabels();
  readonly labels$ = new BehaviorSubject<typeof this._labels>(this._labels);
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.labels$.next(this._labels);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly selectedEvcharge$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._refresh$,
  ]).pipe(
    switchMap(([deviceId, _systemId]) => this.store.select(selectSelectedEvcharge(deviceId))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly statusEvcharge$: Observable<productProperty | undefined> = combineLatest([
    this.selectedEvcharge$,
    this.labels$,
  ]).pipe(
    map(([device, labels]) => {
      if (!device?.status) {
        return undefined;
      }

      const statusCode = getEvchargeTelemetryStatus(device.status);
      const statusKey = getEnumKeyName(EvchargeTelemetryState, statusCode);
      const { label, cssClasses } = getStatusDetails(statusKey, labels);

      return {
        name: labels.status,
        value: label,
        icon: 'info',
        iconCssClasses: 'text-grey-400',
        cssClasses: cssClasses,
      } as productProperty;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectedEvchargeProperties$: Observable<productProperty[]> = combineLatest([
    this.selectedEvcharge$,
    this._externalProperties$.asObservable(),
    this.labels$,
  ]).pipe(
    map(([device, externalProps, labels]) => {
      if (!device) {
        return externalProps ?? [];
      }

      const props: productProperty[] = [];

      if (device.fwVer) {
        props.push({
          name: labels.firmwareVer,
          value: device.fwVer,
          icon: 'memory',
          iconCssClasses: 'text-grey-400',
        });
      }

      if (device.dModel) {
        props.push({
          name: labels.model,
          value: device.dModel,
          icon: 'precision_manufacturing',
          iconCssClasses: 'text-grey-400',
        });
      }

      if (device.limitPower !== undefined) {
        const limitPowerFormatted = `${(device.limitPower / 1000).toPrecision(3)} kW`;
        props.push({
          name: labels.maxPower,
          value: limitPowerFormatted,
          icon: 'bolt',
          iconCssClasses: 'text-grey-400',
        });
      }

      if (device.chargerMode !== undefined) {
        const strategies = getStrategies(this.labels);
        const strategy = strategies.find((s) => s.isEnabled(device.chargerMode));
        const strategyLabel = strategy?.code === device.chargerMode
          ? strategy?.label
          : strategy?.variation?.code === device.chargerMode
            ? strategy?.variation?.label
            : this.labels.unknown;

        props.push({
          name: labels.strategy,
          value: strategyLabel,
          icon: 'info',
          iconCssClasses: 'text-grey-400',
        });
      }

      return externalProps ? [...props, ...externalProps] : props;
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) { }
}
